// src/utils/installIntent.ts
//
// iOS deferred deep linking. Android carries the event id to Play as an
// install referrer (see constants/storeLinks.ts); iOS has no equivalent, so
// before /get redirects an iOS visitor to the App Store we POST an "install
// intent" — the event id plus coarse device signals. On first launch the app
// asks the backend for a matching intent (IP + signals) and opens the event.
// The event page's "Open in App" button also copies a clipboard token as a
// secondary, higher-confidence fallback.
//
// Every read is defensive: this fires in the last milliseconds before a
// navigation away, and a failure here must never block or break the redirect.
// Mirrors the style of src/analytics/utm.ts.

export interface InstallIntentSignals {
  osVersion: string;
  screenMin: number;
  screenMax: number;
  pixelRatio: number;
  locale: string;
  timezone: string;
}

/**
 * iOS detection matching pages/GetApp's platform sniff. iPadOS 13+ masquerades
 * as MacIntel, hence the maxTouchPoints check. Used to keep every part of this
 * feature iOS-only: Android has the install referrer, and writing to a
 * desktop/Android clipboard would only clobber it for nothing.
 */
export const isIosDevice = (): boolean => {
  try {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
      (navigator.platform === 'MacIntel' &&
        (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1)
    );
  } catch {
    return false;
  }
};

// Same env switch the event page uses for its API calls: `?env=dev` on the
// URL targets the dev backend, anything else is production. Deliberately not
// utils/environment.ts — that one persists the choice to localStorage for the
// admin area, which would silently stick a public visitor to dev.
const apiBase = (): string => {
  try {
    const env = new URLSearchParams(window.location.search).get('env');
    if (env === 'dev') return 'https://api.gather-community.com/dev/api';
  } catch {
    // No window / malformed URL — production is the safe default.
  }
  return 'https://api.gather-community.com/api';
};

// "iPhone OS 17_4 like Mac OS X" → "17.4". Empty string when unparseable
// (non-iOS UA, frozen UA, no navigator) — the backend treats it as unknown.
const parseOsVersion = (): string => {
  try {
    const match = /\bOS (\d+(?:[._]\d+)*)/.exec(navigator.userAgent || '');
    return match ? match[1].replace(/_/g, '.') : '';
  } catch {
    return '';
  }
};

/**
 * Coarse device characteristics used to match an install intent to a first
 * app launch. screenMin/screenMax (rather than width/height) so a rotation
 * between tap and launch cannot break the match.
 */
export const buildSignals = (): InstallIntentSignals => {
  let screenMin = 0;
  let screenMax = 0;
  try {
    const { width, height } = window.screen;
    // Rounded: the backend validates these as integers, and the app reports
    // rounded dp — a fractional value here would fail validation server-side.
    screenMin = Math.round(Math.min(width, height)) || 0;
    screenMax = Math.round(Math.max(width, height)) || 0;
  } catch {
    // No window.screen — leave 0/0.
  }

  let pixelRatio = 1;
  try {
    pixelRatio = window.devicePixelRatio || 1;
  } catch {
    // Leave the 1x default.
  }

  let locale = '';
  try {
    locale = (navigator.language || '').toLowerCase();
  } catch {
    // Leave empty.
  }

  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    // Older engines can throw here — leave empty.
  }

  return {
    osVersion: parseOsVersion(),
    screenMin,
    screenMax,
    pixelRatio,
    locale,
    timezone,
  };
};

/**
 * POST of the install intent. Never throws and never rejects — resolves once
 * the request has settled (sent or failed), so a caller about to navigate
 * away can bound-wait on it: the JSON body forces a CORS preflight (two round
 * trips), and preflighted `keepalive` requests are historically unreliable in
 * Safari once navigation starts. `keepalive: true` stays as the second line
 * of defence for callers that do not wait.
 */
export const reportInstallIntent = (eventId: string): Promise<void> => {
  try {
    return fetch(`${apiBase()}/install-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ eventId, signals: buildSignals() }),
    }).then(
      () => undefined,
      () => undefined // Lost intent = plain install, same as before this feature.
    );
  } catch {
    // fetch unavailable / synchronous throw — same graceful degradation.
    return Promise.resolve();
  }
};

/**
 * Clipboard fallback for the "Open in App" button: the app checks the
 * pasteboard for this token on first launch. Format is a contract with the
 * app — do not change it. Must be called from a user gesture (the button
 * tap), or the browser rejects the write; rejection is fine, the IP+signals
 * match above still covers it.
 */
export const writeClipboardToken = (eventId: string): void => {
  try {
    navigator.clipboard.writeText(`gather-event:${eventId}`).catch(() => {
      // Permission denied / unfocused document — clipboard is best-effort.
    });
  } catch {
    // No clipboard API at all (older WebViews) — same story.
  }
};
